'use client';

import { type ReactNode, createContext, useContext, useRef } from 'react';
import { useStore } from 'zustand';
import { i18n } from '@/infrastructure/locales/i18n';
import type { Language } from '@/shared';
import { type LanguageStore, createLanguageStore } from './languageStore';

type LanguageStoreApi = ReturnType<typeof createLanguageStore>;

const LanguageStoreContext = createContext<LanguageStoreApi | undefined>(undefined);

export const LanguageStoreProvider = ({
	children,
	currentLangCode,
}: {
	children: ReactNode;
	currentLangCode: Language;
}) => {
	const storeRef = useRef<LanguageStoreApi>(null);
	if (!storeRef.current) {
		storeRef.current = createLanguageStore(i18n, currentLangCode);
	}

	return <LanguageStoreContext.Provider value={storeRef.current}>{children}</LanguageStoreContext.Provider>;
};

export const useLanguageStore = <T,>(selector: (store: LanguageStore) => T): T => {
	const languageStoreContext = useContext(LanguageStoreContext);

	if (!languageStoreContext) {
		throw new Error(`useLanguageStore must be used within LanguageStoreProvider`);
	}

	return useStore(languageStoreContext, selector);
};
